import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProductCard from '../components/Catalog/ProductCard';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Button from '../components/UI/Button';
import productService from '../services/productService';

const Favorites = () => {
  const { t } = useTranslation();
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFavoriteProducts();
  }, []);

  // Listen for favorites updates from ProductCard
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      fetchFavoriteProducts();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, []);

  const fetchFavoriteProducts = async () => {
    try {
      setLoading(true);
      const savedFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
      
      if (savedFavorites.length > 0) {
        // Fetch all products and filter by favorites
        const response = await productService.getProducts({ limit: 1000 });
        
        // Ensure response.data is an array
        const products = Array.isArray(response.data) ? response.data : [];
        
        if (products.length === 0) {
          console.warn('No products returned from API');
          setFavoriteProducts([]);
          return;
        }
        
        // Convert IDs to strings for comparison to handle both string and number IDs
        const favoritesAsStrings = savedFavorites.map(id => String(id));
        const favorites = products.filter(p => {
          return p && p.id && favoritesAsStrings.includes(String(p.id));
        });
        
        setFavoriteProducts(favorites);
      } else {
        setFavoriteProducts([]);
      }
    } catch (err) {
      console.error('Error fetching favorite products:', err);
      setFavoriteProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = (productId) => {
    const savedFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    // Use String comparison to handle both string and number IDs
    const newFavorites = savedFavorites.filter(id => String(id) !== String(productId));
    localStorage.setItem('favoriteProducts', JSON.stringify(newFavorites));
    setFavoriteProducts(favoriteProducts.filter(p => String(p.id) !== String(productId)));
    
    // Dispatch custom event to notify other components (including Header)
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const clearAllFavorites = () => {
    if (window.confirm(t('favorites.confirm_clear_all'))) {
      localStorage.setItem('favoriteProducts', JSON.stringify([]));
      setFavoriteProducts([]);
      
      // Dispatch custom event to notify other components (including Header)
      window.dispatchEvent(new CustomEvent('favoritesUpdated'));
    }
  };

  const filteredProducts = favoriteProducts.filter(product => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(search) ||
      product.sku.toLowerCase().includes(search) ||
      product.category.toLowerCase().includes(search) ||
      product.material.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-light text-bmb-text mb-2">{t('favorites.title')}</h1>
              <p className="text-gray-600">
                {favoriteProducts.length === 0 
                  ? t('favorites.no_favorites') 
                  : favoriteProducts.length === 1
                    ? t('favorites.count_singular')
                    : t('favorites.count_plural', { count: favoriteProducts.length })
                }
              </p>
            </div>
            
            {favoriteProducts.length > 0 && (
              <button
                onClick={clearAllFavorites}
                className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors text-sm"
              >
                {t('favorites.clear_all')}
              </button>
            )}
          </div>

          {favoriteProducts.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <input
                type="text"
                placeholder={t('favorites.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bmb-text"
              />
            </div>
          )}
        </div>

        {favoriteProducts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h2 className="text-xl font-light text-gray-600 mb-2">{t('favorites.no_favorites')}</h2>
            <p className="text-gray-500 mb-6">
              {t('favorites.no_favorites_description')}
            </p>
            <Link to="/catalog">
              <Button variant="primary">
                {t('favorites.explore_catalog')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">{t('favorites.no_results_for_search', { search: searchTerm })}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="relative">
                    <ProductCard product={product} />
                    <button
                      onClick={() => removeFavorite(product.id)}
                      className="absolute top-2 right-2 p-2 bg-gray-800 rounded-full shadow-md hover:bg-red-600 text-white transition-colors z-10"
                      title={t('product.remove_from_favorites')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;